import React, { useEffect, useState } from 'react';
import {
  Card, CardContent, Typography, CircularProgress,
  Box, Chip, Alert, Tooltip, useTheme
} from '@mui/material';
import { 
  TrendingUp as TrendingUpIcon,
  CheckCircle as SafeIcon,
  Warning as WarningIcon,
  Error as ExceededIcon
} from '@mui/icons-material';
import { budgetsApi } from '../services/api';
import { formatMoney, formatMoneyCompact } from '../utils/money';

/**
 * CircularProgressWithLabel - Circular progress with percentage label
 */
function CircularProgressWithLabel({ value, status, size = 80 }) {
  const getProgressColor = (status) => {
    switch (status) {
      case 'safe': return '#4caf50';
      case 'warning': return '#ff9800';
      case 'critical': return '#ff5722';
      case 'exceeded': return '#f44336';
      default: return '#2196f3';
    }
  };

  const displayValue = Math.min(value, 100);
  const isExceeded = value > 100;

  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      {/* Background circle */}
      <CircularProgress
        variant="determinate"
        value={100}
        size={size}
        thickness={4}
        sx={{ color: 'rgba(0,0,0,0.08)' }}
      />
      {/* Foreground circle */}
      <CircularProgress
        variant="determinate"
        value={displayValue}
        size={size}
        thickness={4}
        sx={{
          color: getProgressColor(status),
          position: 'absolute',
          left: 0,
          '& .MuiCircularProgress-circle': {
            strokeLinecap: 'round',
          },
        }}
      />
      {/* Center label */}
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography
          variant="caption"
          component="div"
          sx={{ 
            fontWeight: 'bold', 
            fontSize: size > 60 ? '0.875rem' : '0.75rem',
            color: isExceeded ? '#f44336' : 'text.primary'
          }}
        >
          {`${Math.round(value)}%`}
        </Typography>
      </Box>
    </Box>
  );
}

/**
 * BudgetTile - Individual budget tile with circular progress
 */
function BudgetTile({ budget }) {
  const getStatusColor = (status) => {
    switch (status) {
      case 'safe': return 'success';
      case 'warning': return 'warning';
      case 'critical': return 'warning';
      case 'exceeded': return 'error';
      default: return 'default';
    }
  };

  const getBorderColor = (status) => {
    switch (status) {
      case 'safe': return 'rgba(76, 175, 80, 0.3)';
      case 'warning': return 'rgba(255, 152, 0, 0.4)';
      case 'critical': return 'rgba(255, 87, 34, 0.5)';
      case 'exceeded': return 'rgba(244, 67, 54, 0.5)';
      default: return 'rgba(0,0,0,0.1)';
    }
  };

  const formatAmount = (amount) => formatMoneyCompact(amount);

  const tooltipContent = (
    <Box sx={{ p: 1 }}>
      <Typography variant="body2" fontWeight="bold" sx={{ color: 'white', mb: 0.5 }}>
        {budget.name || budget.category || 'Overall'}
      </Typography>
      <Typography variant="caption" display="block" sx={{ color: 'rgba(255,255,255,0.9)' }}>
        Spent: {formatMoney(budget.spent)}
      </Typography>
      <Typography variant="caption" display="block" sx={{ color: 'rgba(255,255,255,0.9)' }}>
        Limit: {formatMoney(budget.monthlyLimit)}
      </Typography>
      <Typography variant="caption" display="block" sx={{ 
        color: budget.remaining > 0 ? '#81c784' : '#ef5350',
        fontWeight: 'bold',
        mt: 0.5
      }}>
        {budget.remaining > 0 
          ? `${formatMoney(budget.remaining)} remaining`
          : `${formatMoney(Math.abs(budget.remaining))} over budget`
        }
      </Typography>
      {budget.accountIds && budget.accountIds.length > 0 && (
        <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'rgba(255,255,255,0.7)' }}>
          Accounts: {budget.accountIds.join(', ')}
        </Typography>
      )}
    </Box>
  );

  return (
    <Tooltip 
      title={tooltipContent} 
      arrow 
      placement="top"
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: 'rgba(33, 33, 33, 0.95)',
            '& .MuiTooltip-arrow': {
              color: 'rgba(33, 33, 33, 0.95)',
            },
          },
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          p: 1.5,
          borderRadius: 2,
          border: '2px solid',
          borderColor: getBorderColor(budget.status),
          backgroundColor: 'background.paper',
          transition: 'all 0.2s ease',
          cursor: 'pointer',
          minWidth: 100,
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: 2,
            borderColor: budget.status === 'exceeded' ? 'error.main' : 
                        budget.status === 'warning' || budget.status === 'critical' ? 'warning.main' : 
                        'success.main',
          }
        }}
      >
        <CircularProgressWithLabel 
          value={budget.percentage} 
          status={budget.status}
          size={64}
        />
        <Typography 
          variant="caption" 
          sx={{ 
            mt: 1, 
            fontWeight: 600,
            textAlign: 'center',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%',
            fontSize: '0.7rem'
          }}
        >
          {budget.name || budget.category || 'Overall'}
        </Typography>
        <Typography 
          variant="caption" 
          color="text.secondary"
          sx={{ fontSize: '0.65rem' }}
        >
          {formatAmount(budget.spent)} / {formatAmount(budget.monthlyLimit)}
        </Typography>
      </Box>
    </Tooltip>
  );
}

/**
 * CompactBudgetTile - Budget tile for multi-month view with better spacing
 */
function CompactBudgetTile({ budget }) {
  const getProgressColor = (status) => {
    switch (status) {
      case 'safe': return '#4caf50';
      case 'warning': return '#ff9800';
      case 'critical': return '#ff5722';
      case 'exceeded': return '#f44336';
      default: return '#2196f3';
    }
  };

  const getBorderColor = (status) => {
    switch (status) {
      case 'safe': return 'rgba(76, 175, 80, 0.2)';
      case 'warning': return 'rgba(255, 152, 0, 0.3)';
      case 'critical': return 'rgba(255, 87, 34, 0.4)';
      case 'exceeded': return 'rgba(244, 67, 54, 0.4)';
      default: return 'rgba(0,0,0,0.08)';
    }
  };

  const formatAmount = (amount) => formatMoneyCompact(amount);

  const displayValue = Math.min(budget.percentage, 100);

  const tooltipContent = (
    <Box sx={{ p: 1 }}>
      <Typography variant="body2" fontWeight="bold" sx={{ color: 'white', mb: 0.5 }}>
        {budget.name || budget.category || 'Overall'}
      </Typography>
      <Typography variant="caption" display="block" sx={{ color: 'rgba(255,255,255,0.9)' }}>
        Spent: {formatMoney(budget.spent)}
      </Typography>
      <Typography variant="caption" display="block" sx={{ color: 'rgba(255,255,255,0.9)' }}>
        Limit: {formatMoney(budget.monthlyLimit)}
      </Typography>
      <Typography variant="caption" display="block" sx={{ 
        color: budget.remaining > 0 ? '#81c784' : '#ef5350',
        fontWeight: 'bold',
        mt: 0.5
      }}>
        {budget.remaining > 0 
          ? `${formatMoney(budget.remaining)} remaining`
          : `${formatMoney(Math.abs(budget.remaining))} over budget`
        }
      </Typography>
    </Box>
  );

  return (
    <Tooltip 
      title={tooltipContent} 
      arrow 
      placement="top"
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: 'rgba(33, 33, 33, 0.95)',
            '& .MuiTooltip-arrow': {
              color: 'rgba(33, 33, 33, 0.95)',
            },
          },
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          p: 1.5,
          borderRadius: 2,
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: getBorderColor(budget.status),
          minWidth: 95,
          transition: 'all 0.2s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: 1,
          }
        }}
      >
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          <CircularProgress
            variant="determinate"
            value={100}
            size={56}
            thickness={4}
            sx={{ color: 'rgba(0,0,0,0.08)' }}
          />
          <CircularProgress
            variant="determinate"
            value={displayValue}
            size={56}
            thickness={4}
            sx={{
              color: getProgressColor(budget.status),
              position: 'absolute',
              left: 0,
              '& .MuiCircularProgress-circle': {
                strokeLinecap: 'round',
              },
            }}
          />
          <Box
            sx={{
              top: 0, left: 0, bottom: 0, right: 0,
              position: 'absolute',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography
              variant="caption"
              sx={{ 
                fontWeight: 'bold', 
                fontSize: '0.75rem',
                color: budget.percentage > 100 ? '#f44336' : 'text.primary'
              }}
            >
              {Math.round(budget.percentage)}%
            </Typography>
          </Box>
        </Box>
        <Typography 
          variant="caption" 
          sx={{ 
            mt: 1, 
            fontWeight: 600,
            textAlign: 'center',
            lineHeight: 1.3,
            fontSize: '0.7rem',
            maxWidth: 85,
            wordWrap: 'break-word',
            hyphens: 'auto'
          }}
        >
          {budget.name || budget.category || 'Overall'}
        </Typography>
        <Typography 
          variant="caption" 
          color="text.secondary"
          sx={{ fontSize: '0.65rem', mt: 0.25 }}
        >
          {formatAmount(budget.spent)} / {formatAmount(budget.monthlyLimit)}
        </Typography>
      </Box>
    </Tooltip>
  );
}

/**
 * MonthBudgetRow - Single month's budget row with overall bar and tiles (compact)
 */
function MonthBudgetRow({ monthData, theme, isCompact = false }) {
  const budgets = monthData.budgetStatus || [];
  
  // Calculate summary stats
  const summary = budgets.reduce((acc, budget) => {
    acc.totalSpent += budget.spent;
    acc.totalLimit += budget.monthlyLimit;
    if (budget.status === 'safe') acc.safe++;
    else if (budget.status === 'warning' || budget.status === 'critical') acc.warning++;
    else if (budget.status === 'exceeded') acc.exceeded++;
    return acc;
  }, { safe: 0, warning: 0, exceeded: 0, totalSpent: 0, totalLimit: 0 });

  const overallPercentage = summary.totalLimit > 0 
    ? Math.round((summary.totalSpent / summary.totalLimit) * 100) 
    : 0;

  return (
    <Box 
      sx={{ 
        mb: 2, 
        p: 2, 
        borderRadius: 2, 
        backgroundColor: monthData.isCurrent 
          ? (theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.1)' : 'rgba(25, 118, 210, 0.04)')
          : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)'),
        border: monthData.isCurrent ? '2px solid' : '1px solid',
        borderColor: monthData.isCurrent ? 'primary.main' : 'divider'
      }}
    >
      {/* Header row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
        {/* Month label */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 100 }}>
          <Typography 
            variant="body2" 
            fontWeight={monthData.isCurrent ? 700 : 500}
            color={monthData.isCurrent ? 'primary.main' : 'text.primary'}
          >
            {monthData.label}
          </Typography>
          {monthData.isCurrent && (
            <Chip label="Current" size="small" color="primary" sx={{ height: 16, fontSize: '0.55rem' }} />
          )}
        </Box>
        
        {/* Overall spending bar - inline */}
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            Overall Spending
          </Typography>
          <Box 
            sx={{ 
              flex: 1,
              height: 6, 
              borderRadius: 3, 
              backgroundColor: 'rgba(0,0,0,0.1)',
              overflow: 'hidden',
              minWidth: 100
            }}
          >
            <Box 
              sx={{ 
                height: '100%', 
                width: `${Math.min(overallPercentage, 100)}%`,
                borderRadius: 3,
                backgroundColor: overallPercentage > 100 ? '#f44336' : 
                                overallPercentage > 80 ? '#ff9800' : 
                                '#4caf50',
                transition: 'width 0.5s ease'
              }} 
            />
          </Box>
          <Typography 
            variant="caption" 
            fontWeight="bold"
            sx={{ 
              color: overallPercentage > 100 ? 'error.main' : 
                     overallPercentage > 80 ? 'warning.main' : 
                     'success.main',
              minWidth: 35
            }}
          >
            {overallPercentage}%
          </Typography>
        </Box>

        {/* Amount */}
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', whiteSpace: 'nowrap' }}>
          {formatMoney(summary.totalSpent, { decimals: 0 })} / {formatMoney(summary.totalLimit, { decimals: 0 })}
        </Typography>

        {/* Summary chips */}
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {summary.safe > 0 && (
            <Chip 
              icon={<SafeIcon sx={{ fontSize: 10 }} />}
              label={summary.safe}
              size="small"
              color="success"
              variant="outlined"
              sx={{ height: 18, fontSize: '0.55rem', '& .MuiChip-icon': { ml: 0.5 } }}
            />
          )}
          {summary.warning > 0 && (
            <Chip 
              icon={<WarningIcon sx={{ fontSize: 10 }} />}
              label={summary.warning}
              size="small"
              color="warning"
              variant="outlined"
              sx={{ height: 18, fontSize: '0.55rem', '& .MuiChip-icon': { ml: 0.5 } }}
            />
          )}
          {summary.exceeded > 0 && (
            <Chip 
              icon={<ExceededIcon sx={{ fontSize: 10 }} />}
              label={summary.exceeded}
              size="small"
              color="error"
              variant="outlined"
              sx={{ height: 18, fontSize: '0.55rem', '& .MuiChip-icon': { ml: 0.5 } }}
            />
          )}
        </Box>
      </Box>

      {/* Budget tiles - with more breathing room, sorted by name for consistency */}
      <Box 
        sx={{ 
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.5,
          justifyContent: 'flex-start',
          mt: 1,
        }}
      >
        {[...budgets]
          .sort((a, b) => (a.name || a.category || '').localeCompare(b.name || b.category || ''))
          .map((budget, index) => (
            <CompactBudgetTile key={budget.id || index} budget={budget} />
          ))
        }
      </Box>
    </Box>
  );
}

/**
 * BudgetWidget - Displays budget status with circular progress indicators
 * Supports both single month view and 3-month comparison view
 */
function BudgetWidget({ budgetStatus: propBudgetStatus, historicalBudgets, loading: propLoading }) {
  const [budgetStatus, setBudgetStatus] = useState(propBudgetStatus || []);
  const [loading, setLoading] = useState(propLoading ?? !propBudgetStatus);
  const [error, setError] = useState(null);
  const theme = useTheme();

  // Update state when props change
  useEffect(() => {
    if (propBudgetStatus !== undefined) {
      setBudgetStatus(propBudgetStatus);
      setLoading(false);
    }
  }, [propBudgetStatus]);

  useEffect(() => {
    if (propLoading !== undefined) {
      setLoading(propLoading);
    }
  }, [propLoading]);

  // Only fetch if no prop data provided
  useEffect(() => {
    if (propBudgetStatus === undefined) {
    loadBudgetStatus();
    }
  }, [propBudgetStatus]);

  const loadBudgetStatus = async () => {
    try {
      setLoading(true);
      const response = await budgetsApi.getStatus();
      setBudgetStatus(response.data.budgetStatus || []);
      setError(null);
    } catch (err) {
      console.error('Error loading budget status:', err);
      setError('Failed to load budget status');
    } finally {
      setLoading(false);
    }
  };

  // Calculate summary stats for current/single month
  const summary = budgetStatus.reduce((acc, budget) => {
    acc.totalSpent += budget.spent;
    acc.totalLimit += budget.monthlyLimit;
    if (budget.status === 'safe') acc.safe++;
    else if (budget.status === 'warning' || budget.status === 'critical') acc.warning++;
    else if (budget.status === 'exceeded') acc.exceeded++;
    return acc;
  }, { safe: 0, warning: 0, exceeded: 0, totalSpent: 0, totalLimit: 0 });

  const overallPercentage = summary.totalLimit > 0 
    ? Math.round((summary.totalSpent / summary.totalLimit) * 100) 
    : 0;

  if (loading) {
    return (
      <Card sx={{ height: '100%', minHeight: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={40} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Loading budgets...
          </Typography>
        </Box>
      </Card>
    );
  }

  // 3-month comparison view
  if (historicalBudgets && historicalBudgets.length > 0) {
    return (
      <Card sx={{ display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          p: 2,
          '&:last-child': { pb: 2 }
        }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <TrendingUpIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">Budget Status (3-Month View)</Typography>
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          )}

          {/* Render each month's budget row - no scroll, full height */}
          <Box>
            {historicalBudgets.map((monthData, index) => (
              <MonthBudgetRow 
                key={`${monthData.year}-${monthData.month}`} 
                monthData={monthData} 
                theme={theme}
              />
            ))}
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Single month view (original)
  return (
    <Card sx={{ height: '100%', minHeight: 300, display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ 
        flexGrow: 1, 
        display: 'flex', 
        flexDirection: 'column', 
        p: 2,
        '&:last-child': { pb: 2 }
      }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <TrendingUpIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6">Budget Status</Typography>
          </Box>
          
          {/* Summary chips */}
          {budgetStatus.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {summary.safe > 0 && (
                <Chip 
                  icon={<SafeIcon sx={{ fontSize: 16 }} />}
                  label={summary.safe}
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={{ height: 24 }}
                />
              )}
              {summary.warning > 0 && (
                <Chip 
                  icon={<WarningIcon sx={{ fontSize: 16 }} />}
                  label={summary.warning}
                  size="small"
                  color="warning"
                  variant="outlined"
                  sx={{ height: 24 }}
                />
              )}
              {summary.exceeded > 0 && (
                <Chip 
                  icon={<ExceededIcon sx={{ fontSize: 16 }} />}
                  label={summary.exceeded}
                  size="small"
                  color="error"
                  variant="outlined"
                  sx={{ height: 24 }}
                />
              )}
            </Box>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        {budgetStatus.length === 0 ? (
          <Box sx={{ 
            flexGrow: 1, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            flexDirection: 'column' 
          }}>
            <TrendingUpIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="body2" color="text.secondary" align="center">
              No budgets set up yet
            </Typography>
            <Typography variant="caption" color="text.secondary" align="center" sx={{ mt: 1 }}>
              Go to Settings → Budgets to create your first budget
            </Typography>
          </Box>
        ) : (
          <>
            {/* Overall summary bar */}
            <Box 
              sx={{ 
                mb: 2, 
                p: 1.5, 
                borderRadius: 2, 
                backgroundColor: theme.palette.mode === 'dark' 
                  ? 'rgba(255,255,255,0.05)' 
                  : 'rgba(0,0,0,0.02)',
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" fontWeight="500">
                  Overall Spending
                  </Typography>
                <Typography 
                  variant="body2" 
                  fontWeight="bold"
                  sx={{ 
                    color: overallPercentage > 100 ? 'error.main' : 
                           overallPercentage > 80 ? 'warning.main' : 
                           'success.main'
                  }}
                >
                  {overallPercentage}%
                </Typography>
              </Box>
              <Box 
                sx={{ 
                  mt: 1, 
                  height: 8, 
                  borderRadius: 4, 
                  backgroundColor: 'rgba(0,0,0,0.1)',
                  overflow: 'hidden'
                }}
              >
                <Box 
                  sx={{ 
                    height: '100%', 
                    width: `${Math.min(overallPercentage, 100)}%`,
                    borderRadius: 4,
                    backgroundColor: overallPercentage > 100 ? '#f44336' : 
                                    overallPercentage > 80 ? '#ff9800' : 
                                    '#4caf50',
                    transition: 'width 0.5s ease'
                  }} 
                />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {formatMoney(summary.totalSpent, { decimals: 0 })} of {formatMoney(summary.totalLimit, { decimals: 0 })} total budget
              </Typography>
            </Box>

            {/* Budget tiles grid */}
            <Box 
              sx={{ 
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1.5,
                justifyContent: 'flex-start',
                flexGrow: 1,
                alignContent: 'flex-start',
                overflowY: 'auto',
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(0,0,0,0.2)',
                  borderRadius: '3px',
                },
              }}
            >
              {/* Sort by name for consistent ordering */}
              {[...budgetStatus]
                .sort((a, b) => (a.name || a.category || '').localeCompare(b.name || b.category || ''))
                .map((budget, index) => (
                  <BudgetTile key={budget.id || index} budget={budget} />
                ))
              }
          </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default BudgetWidget;
